/**
 * Collapse room walls that describe the same physical surface from the
 * same side. Some upstream structures (e.g. when alignment splits a
 * wall into stacked structure-wall segments) end up emitting two room
 * walls at the same x/z line and the same inward normal, with one's
 * extent fully covered by the other's. Auto-fill cabinets land on both
 * and collide.
 *
 * Strategy: group walls by (line, inward-normal). Inside each group,
 * project the walls onto the line and merge overlapping / touching
 * extents into one cluster — keeping the longest wall as the
 * representative for each cluster (it covers the cluster's union when
 * the cluster contains nested duplicates, which is the only pattern
 * we've seen so far in real data). Disjoint segments on the same line
 * stay as separate walls.
 *
 * Pure: returns a new array; the caller owns wall identity.
 */

const { getWallAxes } = require('./wallMath');

const COPLANAR_TOL = 0.05; // 5 cm — perpendicular distance to the same line
const PARALLEL_TOL = 0.05; // sin(angle) tolerance — ~3°
const NORMAL_TOL = 0.05;
const MERGE_GAP_TOL = 0.05; // segments closer than this along the line merge

function dedupeRoomWalls(walls) {
  if (!Array.isArray(walls) || walls.length === 0) return walls || [];

  // Virtual walls (peninsula synthesizer output) are pass-through. They
  // can be co-planar with a real wall (the peninsula's body sits along
  // a line that an unrelated real wall happens to share), and merging
  // them into the real wall destroys the peninsula's identity — then if
  // that real wall later fails the kitchen-section filter (open-plan
  // rooms with stray walls outside the kitchen) the peninsula
  // disappears entirely. Keep them untouched.
  const virtuals = [];
  const dedupable = [];
  for (const wall of walls) {
    if (wall?.isVirtual) virtuals.push(wall);
    else dedupable.push(wall);
  }

  const items = [];
  for (const wall of dedupable) {
    const axes = getWallAxes(wall);
    if (!axes) {
      // Wall has no usable alignment — leave it as-is, dedupe doesn't apply.
      items.push({ wall, axes: null });
      continue;
    }
    items.push({ wall, axes });
  }

  // Group walls by (line, inward-normal). Two walls share a group if
  // their direction vectors are parallel (dot ≈ ±1), their inward
  // normals match exactly (dot ≈ +1), and one's start lies on the
  // other's line within COPLANAR_TOL.
  const groups = [];
  for (const item of items) {
    if (!item.axes) {
      groups.push([item]);
      continue;
    }
    let placed = false;
    for (const group of groups) {
      const ref = group[0].axes;
      if (!ref) continue;
      const dirDot =
        ref.wallDir.x * item.axes.wallDir.x +
        ref.wallDir.z * item.axes.wallDir.z;
      if (Math.abs(Math.abs(dirDot) - 1) > PARALLEL_TOL) continue;
      const normalDot =
        ref.wallNormal.x * item.axes.wallNormal.x +
        ref.wallNormal.z * item.axes.wallNormal.z;
      if (Math.abs(normalDot - 1) > NORMAL_TOL) continue;
      const dx = item.axes.start.x - ref.start.x;
      const dz = item.axes.start.z - ref.start.z;
      const perp = Math.abs(dx * ref.wallNormal.x + dz * ref.wallNormal.z);
      if (perp > COPLANAR_TOL) continue;
      group.push(item);
      placed = true;
      break;
    }
    if (!placed) groups.push([item]);
  }

  const result = [];
  for (const group of groups) {
    if (group.length === 1 || !group[0].axes) {
      result.push(group[0].wall);
      continue;
    }
    // Project each wall onto the group's reference axis to get a 1D
    // range, then merge overlapping/touching ranges into clusters.
    const ref = group[0].axes;
    const projOf = (pt) =>
      (pt.x - ref.start.x) * ref.wallDir.x +
      (pt.z - ref.start.z) * ref.wallDir.z;
    const ranges = group.map(({ wall, axes }) => {
      const a = projOf(axes.start);
      const b = projOf(axes.end);
      return {
        wall,
        min: Math.min(a, b),
        max: Math.max(a, b),
        len: axes.wallLen,
      };
    });
    ranges.sort((a, b) => a.min - b.min);

    let cluster = [ranges[0]];
    let clusterMax = ranges[0].max;
    const finalize = (c) => {
      // Keep the longest wall in the cluster — when one wall fully
      // contains the others (the observed real-world pattern) the
      // longest is also the one whose endpoints span the union.
      const best = c.reduce((a, b) => (a.len >= b.len ? a : b));
      result.push(best.wall);
    };
    for (let i = 1; i < ranges.length; i++) {
      if (ranges[i].min <= clusterMax + MERGE_GAP_TOL) {
        cluster.push(ranges[i]);
        clusterMax = Math.max(clusterMax, ranges[i].max);
      } else {
        finalize(cluster);
        cluster = [ranges[i]];
        clusterMax = ranges[i].max;
      }
    }
    finalize(cluster);
  }
  // Re-attach virtual walls (excluded from grouping above).
  return [...result, ...virtuals];
}

module.exports = { dedupeRoomWalls };
